// External dependencies
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import ExpandMoreSharpIcon from "@mui/icons-material/ExpandMoreSharp";

// Components
import LocationAutocomplete from "components/inputs/LocationAutocomplete/LocationAutocomplete.component";

// Helpers
import { getAddressComponents } from "helpers/google-helpers";
import {
  addOrUpdateRouterSlugValues,
  getPathnameItem,
  replaceSlugAndQuery,
} from "helpers/inventory-filters";

// Styles
import { useStyles } from "./LocationSearch.styles";

const LocationSearch = ({ afterSubmit, clearInventoryCards, location }) => {
  const dispatch = useDispatch();
  const desktopView = useSelector((state) => state.global.desktopView);
  const classes = useStyles();
  const router = useRouter();
  const userLocation = useSelector((s) => s.inventory.userLocation);

  const cityFromPath =
    getPathnameItem(router.query.slug, "city") ?? location.city;
  const provinceFromPath =
    getPathnameItem(router.query.slug, "province") ?? location.province;
  const handleLocation = (loc) => {
    const {
      formattedAddress,
      city,
      province,
      place_id: placeId,
    } = getAddressComponents(loc.address_components);
    dispatch({
      type: "inventory/setUserLocation",
      payload: {
        lat: loc.geometry.location.lat(),
        lng: loc.geometry.location.lng(),
        city,
        province,
        fullName: formattedAddress,
        placeId,
      },
    });
    if (window.google.map) {
      window.google.map.fitBounds(loc.geometry.viewport);
      dispatch({ type: "inventory/setUserLocationChanged", payload: true });
    }
    if (province && city) {
      clearInventoryCards();
      const newSlug = addOrUpdateRouterSlugValues(router.query.slug, {
        province,
        city,
      });
      replaceSlugAndQuery(router, newSlug, router.query);
    }
    afterSubmit();
  };

  return (
    <LocationAutocomplete
      shareState // this is so we can use a reducer to keep both inputs in sync
      size="small"
      types={["(regions)"]}
      handleLocation={handleLocation}
      textFieldProps={{
        label: desktopView ? "Location" : "",
        placeholder: !desktopView ? "Location" : "",
        variant: "outlined",
        className: classes.inputField,
      }}
      popupIcon={<ExpandMoreSharpIcon />}
      initialValue={
        userLocation?.fullName
          ? userLocation.fullName
          : { city: cityFromPath, province: provinceFromPath.toUpperCase() }
      }
      disableClearable
      className={classes.search}
      classes={{ popper: classes.popper }}
    />
  );
};

LocationSearch.defaultProps = {
  afterSubmit: () => {},
};

LocationSearch.propTypes = {
  afterSubmit: PropTypes.func,
};

export default LocationSearch;
